/**
 * ¿HAY ALGO AVISADO EN ESTA LÍNEA? — los candidatos de la pregunta al trabajador.
 *
 * Los focos del sector se piden en cuanto se elige el sector (no al llegar a la
 * pregunta), con reintentos: con cobertura floja, la primera petición se pierde
 * y la pregunta no puede perderse con ella (D20). La regla de enlace
 * (`pickFocusForQuestion`) dice sobre cuáles preguntar: su misma línea, vivos o
 * resueltos hace poco. Si hay VARIOS en la línea (dos plagas, o la misma a más
 * de 30 m), se le enseñan todos con su foto y elige él (D24): «¿es alguno de
 * estos?». Y si en esa línea no hay foco pero SÍ un parte de las últimas 48 h
 * aún sin confirmar, se pregunta por ese parte.
 *
 * Solo lee su sector; el alcance lo da el propio parte que se está haciendo.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { diffDaysIso, addDaysIso } from "@/lib/reports/dates";
import { fetchBlockFociContext, linkContextOf, summariesOf, type BlockContext } from "@/hooks/useFocusLinkProposal";
import { NEIGHBOUR_LINES, pickFocusForQuestion, type QuestionPlace } from "@/lib/health/focusLinking";
import type { FollowUpCandidate } from "@/lib/pests/followUp";

const PENDING_DAYS = 2;
/** Más de esto ya no es una pregunta, es una lista: se enseñan los primeros por actividad. */
const MAX_OPTIONS = 3;

interface PendingReport {
  id: string;
  date: string;
  block_row_id: string | null;
  notes: string | null;
  photo_url: string | null;
  photo_urls: string[] | null;
}

async function fetchContextAndPending(companyId: string, blockId: string): Promise<{ ctx: BlockContext; pending: PendingReport[] }> {
  const ctx = await fetchBlockFociContext(companyId, blockId);
  // No fatal: sin esto solo se pierde la pista sobre partes sin confirmar.
  const since = addDaysIso(new Date().toISOString().slice(0, 10), -PENDING_DAYS);
  const { data } = await supabase
    .from("pest_scouting_sessions")
    .select("id, date, block_row_id, notes, photo_url, photo_urls")
    .eq("company_id", companyId)
    .eq("block_id", blockId)
    .eq("triage_status", "pending_triage")
    .gte("date", since)
    .order("date", { ascending: false })
    .order("created_at", { ascending: false })
    .limit(20);
  return { ctx, pending: ((data ?? []) as PendingReport[]) };
}

const lineOf = (p: PendingReport): number | null => {
  const m = /L[ií]nea?\s*[:：]\s*(\d+)/i.exec(p.notes ?? "");
  return m ? Number(m[1]) : null;
};

export function useLineFollowUpCandidate(place: QuestionPlace | null, enabled: boolean) {
  const { company } = useAuth();
  const blockId = place?.blockId ?? null;
  const query = useQuery({
    queryKey: ["line-followup", company?.id, blockId],
    enabled: enabled && !!company?.id && !!blockId,
    staleTime: 60_000,
    retry: 2,
    queryFn: () => fetchContextAndPending(company!.id, blockId!),
  });

  const candidates = useMemo<FollowUpCandidate[]>(() => {
    if (!query.data || !place) return [];
    // La MISMA regla, aplicada una y otra vez quitando el elegido: así salen todos
    // los sitios de la línea en el orden en que la regla los prefiere.
    let restantes = summariesOf(query.data.ctx, null);
    const ctx = linkContextOf(query.data.ctx, place.blockId);
    const out: FollowUpCandidate[] = [];
    const recoger = (probe: QuestionPlace, gap: number) => {
      while (out.length < MAX_OPTIONS) {
        const focus = pickFocusForQuestion(probe, restantes, ctx);
        if (!focus) break;
        restantes = restantes.filter((f) => f.id !== focus.id);
        if (!focus.pestName) continue;
        out.push({
          focusId: focus.id,
          lineNumber: probe.lineNumber ?? null,
          lineGap: gap,
          pestName: focus.pestName,
          lastDate: focus.lastDate,
          daysAgo: Math.max(0, diffDaysIso(focus.lastDate, place.date)),
          photoUrl: focus.lastPhotoUrl ?? null,
        });
      }
    };
    recoger(place, 0);
    // En la misma línea no hay nada: ¿y AL LADO (±1, ±2, las «vecinas» de la regla de enlace)?
    // Se le dice que es al lado, y su respuesta es una pista igual de válida para quien confirma.
    if (out.length === 0 && place.lineNumber != null) {
      for (let gap = 1; gap <= NEIGHBOUR_LINES && out.length === 0; gap++) {
        for (const sign of [-1, 1]) {
          const linea = place.lineNumber + sign * gap;
          if (linea < 1) continue;
          recoger({ ...place, rowId: null, lineNumber: linea }, gap);
        }
      }
    }
    if (out.length > 0) return out;
    // Sin foco en la línea: ¿un parte reciente sin confirmar, en ESA línea?
    const pending = query.data.pending.find((p) => (place.rowId && p.block_row_id === place.rowId) || (place.lineNumber != null && lineOf(p) === place.lineNumber));
    if (!pending) return [];
    return [{
      focusId: null,
      sessionId: pending.id,
      pestName: "",
      lastDate: pending.date,
      daysAgo: Math.max(0, diffDaysIso(pending.date, place.date)),
      photoUrl: pending.photo_urls?.[0] ?? pending.photo_url ?? null,
    }];
  }, [query.data, place]);

  const status: "off" | "loading" | "ready" | "failed" = !enabled || !blockId ? "off" : query.isError ? "failed" : query.isLoading ? "loading" : "ready";
  return { candidate: candidates[0] ?? null, candidates, status };
}
